import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Star, Gift, Award, Percent, LayoutDashboard,
  LogOut, Check, X, Trash2, Plus, RefreshCw, Settings, Image, Instagram,
  FileDown, FileSpreadsheet, Mail
} from "lucide-react";
// PDF/Excel exports handled by backend
import { TrophyCertificate, TrophyCard, GiftVoucher } from "@/components/TrophyCertificate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Resolve media URLs - converts old preview URLs to relative paths
const resolveMediaUrl = (url) => {
  if (!url) return url;
  // Strip any old preview domain prefix, keep only the /api/files/... path
  const match = url.match(/\/api\/files\/.+/);
  if (match) return `${BACKEND_URL}${match[0]}`;
  return url;
};

// Login
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      onLogin(res.data);
      toast.success(`Welcome, ${res.data.name || username}!`);
    } catch {
      toast.error("Invalid credentials");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0]" data-testid="admin-login">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="font-heading text-3xl text-center text-[#0A192F]">Admin Login</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input type="text" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} className="form-input" data-testid="admin-username" />
            <Input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="form-input" data-testid="admin-password" />
            <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="admin-login-btn">{loading ? "Logging in..." : "Login"}</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Stats Cards
const StatsCards = ({ stats }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="stats-cards">
    {[
      { label: "Total Guests", value: stats.total_guests, icon: Users, color: "text-[#FF6B6B]" },
      { label: "Average Rating", value: stats.average_rating, icon: Star, color: "text-[#D4AF37] fill-[#D4AF37]" },
      { label: "Gift Winners", value: stats.gift_winners, icon: Gift, color: "text-[#D4AF37]" },
      { label: "Pending Reviews", value: stats.pending_reviews, icon: Star, color: "text-[#FF6B6B]" },
    ].map(({ label, value, icon: Icon, color }) => (
      <Card key={label} className="card-flamingo">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#4A5568]">{label}</p>
              <p className="text-3xl font-heading text-[#0A192F]">{value}</p>
            </div>
            <Icon className={color} size={32} />
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

// Delete Confirmation Button
const DeleteButton = ({ onDelete, label = "item", testId }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="destructive" size="sm" data-testid={testId}><Trash2 size={14} /></Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="font-heading text-xl">Are you sure?</AlertDialogTitle>
        <AlertDialogDescription>This action cannot be undone. This will permanently delete this {label}.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700" data-testid={`${testId}-confirm`}>Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Guests Tab
const GuestsTab = ({ guests, reviews, giftWinners, trophyWinners, onRefresh, viewOnly }) => {
  const handleDelete = async (guestId) => {
    try {
      await axios.delete(`${API}/guests/${guestId}`);
      toast.success("Guest deleted");
      onRefresh();
    } catch { toast.error("Failed to delete guest"); }
  };

  const downloadFile = async (url, filename, mimeType) => {
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: mimeType });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      toast.success(`${filename} downloaded! Check your Downloads folder.`);
    } catch {
      toast.error("Failed to download report");
    }
  };

  const exportPDF = () => downloadFile(`${API}/export/pdf`, 'flamingo_inn_guest_report.pdf', 'application/pdf');
  const exportExcel = () => downloadFile(`${API}/export/excel`, 'flamingo_inn_guest_report.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

  return (
    <div data-testid="guests-tab">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-heading text-2xl text-[#0A192F]">Registered Guests</h3>
        <div className="flex gap-2">
          <Button onClick={exportPDF} variant="outline" size="sm" data-testid="export-pdf-btn">
            <FileDown size={16} className="mr-2" /> PDF
          </Button>
          <Button onClick={exportExcel} variant="outline" size="sm" data-testid="export-excel-btn">
            <FileSpreadsheet size={16} className="mr-2" /> Excel
          </Button>
          <Button onClick={onRefresh} variant="outline" size="sm" data-testid="refresh-guests">
            <RefreshCw size={16} className="mr-2" /> Refresh
          </Button>
        </div>
      </div>
      <Card className="card-flamingo">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Room</TableHead><TableHead>Registered</TableHead><TableHead>Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {guests.map((guest) => (
              <TableRow key={guest.id} data-testid={`guest-row-${guest.id}`}>
                <TableCell className="font-medium">{guest.name}</TableCell>
                <TableCell>{guest.email}</TableCell>
                <TableCell>{guest.room_number || "—"}</TableCell>
                <TableCell>{new Date(guest.registered_at).toLocaleDateString()}</TableCell>
                <TableCell>{!viewOnly && <DeleteButton onDelete={() => handleDelete(guest.id)} label="guest" testId={`delete-guest-${guest.id}`} />}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// Reviews Tab
const ReviewsTab = ({ reviews, onRefresh, viewOnly }) => {
  const handleApprove = async (reviewId) => {
    try { await axios.put(`${API}/reviews/${reviewId}/approve`); toast.success("Review approved"); onRefresh(); } catch { toast.error("Failed to approve review"); }
  };
  const handleDelete = async (reviewId) => {
    try { await axios.delete(`${API}/reviews/${reviewId}`); toast.success("Review deleted"); onRefresh(); } catch { toast.error("Failed to delete review"); }
  };

  return (
    <div data-testid="reviews-tab">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-heading text-2xl text-[#0A192F]">Guest Reviews</h3>
        <Button onClick={onRefresh} variant="outline" size="sm" data-testid="refresh-reviews"><RefreshCw size={16} className="mr-2" /> Refresh</Button>
      </div>
      <Card className="card-flamingo">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Guest</TableHead><TableHead>Rating</TableHead><TableHead>Review</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {reviews.map((review) => (
              <TableRow key={review.id} data-testid={`review-row-${review.id}`}>
                <TableCell className="font-medium">{review.guest_name}</TableCell>
                <TableCell>
                  <div className="flex gap-1">{[...Array(5)].map((_, i) => <Star key={i} size={14} className={i < review.rating ? "fill-[#D4AF37] text-[#D4AF37]" : "text-gray-300"} />)}</div>
                </TableCell>
                <TableCell className="max-w-xs truncate">{review.text}</TableCell>
                <TableCell>
                  <span className={`px-2 py-1 text-xs rounded ${review.approved ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                    {review.approved ? "Approved" : "Pending"}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    {!viewOnly && !review.approved && <Button variant="outline" size="sm" onClick={() => handleApprove(review.id)} className="text-green-600" data-testid={`approve-review-${review.id}`}><Check size={14} /></Button>}
                    {!viewOnly && <DeleteButton onDelete={() => handleDelete(review.id)} label="review" testId={`delete-review-${review.id}`} />}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// Specials Tab
const SpecialsTab = ({ specials, onRefresh, viewOnly }) => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: "", description: "", discount_percent: 20, valid_until: "", room_type: "", image_url: "", listing_type: "special" });
  const [roomTypes, setRoomTypes] = useState([]);
  const [newRoomType, setNewRoomType] = useState("");
  const [showRoomManager, setShowRoomManager] = useState(false);
  const [uploadingSpecial, setUploadingSpecial] = useState(false);
  const [specialImageMode, setSpecialImageMode] = useState("url");

  useEffect(() => { fetchRoomTypes(); }, []);

  const fetchRoomTypes = async () => {
    try {
      const res = await axios.get(`${API}/room-types`);
      setRoomTypes(res.data);
      if (res.data.length > 0 && !formData.room_type) {
        setFormData((prev) => ({ ...prev, room_type: res.data[0].name }));
      }
    } catch { /* use defaults */ }
  };

  const handleAddRoomType = async () => {
    if (!newRoomType.trim()) return;
    try {
      await axios.post(`${API}/room-types?name=${encodeURIComponent(newRoomType.trim())}`);
      toast.success(`Room type "${newRoomType.trim()}" added`);
      setNewRoomType("");
      fetchRoomTypes();
    } catch { toast.error("Failed to add room type"); }
  };

  const handleDeleteRoomType = async (name) => {
    try {
      await axios.delete(`${API}/room-types/${encodeURIComponent(name)}`);
      toast.success(`Room type "${name}" removed`);
      fetchRoomTypes();
    } catch { toast.error("Failed to delete room type"); }
  };

  const handleCreate = async (sendEmails = true) => {
    try {
      await axios.post(`${API}/specials?send_emails=${sendEmails}`, formData);
      toast.success(sendEmails ? "Special created and emails sent to all guests!" : "Special created (no emails sent).");
      setShowForm(false);
      setFormData({ title: "", description: "", discount_percent: 20, valid_until: "", room_type: roomTypes[0]?.name || "", image_url: "", listing_type: "special" });
      setSpecialImageMode("url");
      onRefresh();
    } catch { toast.error("Failed to create special"); }
  };

  const handleSpecialImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSpecial(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const imageUrl = res.data.url;
      setFormData((prev) => ({ ...prev, image_url: imageUrl }));
      toast.success("Image uploaded!");
    } catch { toast.error("Upload failed"); }
    setUploadingSpecial(false);
  };

  const handleDeactivate = async (specialId) => {
    try { await axios.put(`${API}/specials/${specialId}/deactivate`); toast.success("Special deactivated"); onRefresh(); } catch { toast.error("Failed to deactivate special"); }
  };

  return (
    <div data-testid="specials-tab">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-heading text-2xl text-[#0A192F]">Weekly Specials</h3>
        <div className="flex gap-2">
          {!viewOnly && <Dialog open={showRoomManager} onOpenChange={setShowRoomManager}>
            <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="manage-room-types-btn"><Settings size={16} className="mr-2" /> Room Types</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading text-2xl">Manage Room Types</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex gap-2">
                  <Input placeholder="New room type name" value={newRoomType} onChange={(e) => setNewRoomType(e.target.value)} data-testid="new-room-type-input"
                    onKeyDown={(e) => e.key === "Enter" && handleAddRoomType()} />
                  <Button onClick={handleAddRoomType} className="btn-primary whitespace-nowrap" data-testid="add-room-type-btn"><Plus size={16} className="mr-1" /> Add</Button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {roomTypes.map((rt) => (
                    <div key={rt.name} className="flex items-center justify-between bg-[#F7F5F0] px-4 py-2" data-testid={`room-type-${rt.name}`}>
                      <span className="text-[#0A192F] font-medium">{rt.name}</span>
                      <DeleteButton onDelete={() => handleDeleteRoomType(rt.name)} label="room type" testId={`delete-room-type-${rt.name}`} />
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>}
          {!viewOnly && <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild><Button className="btn-primary" data-testid="create-special-btn"><Plus size={16} className="mr-2" /> Create Listing</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading text-2xl">{formData.listing_type === "special" ? "Create Weekly Special" : "Create Regular Listing"}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                {/* Listing Type Toggle */}
                <div className="flex gap-2 bg-[#F7F5F0] p-1 rounded">
                  <button onClick={() => setFormData({ ...formData, listing_type: "special", discount_percent: 20 })}
                    className={`flex-1 py-2 text-sm font-medium transition-colors rounded ${formData.listing_type === "special" ? "bg-white text-[#0A192F] shadow-sm" : "text-[#4A5568]"}`}
                    data-testid="listing-type-special">
                    <Percent size={14} className="inline mr-1" /> Special
                  </button>
                  <button onClick={() => setFormData({ ...formData, listing_type: "regular", discount_percent: 0 })}
                    className={`flex-1 py-2 text-sm font-medium transition-colors rounded ${formData.listing_type === "regular" ? "bg-white text-[#0A192F] shadow-sm" : "text-[#4A5568]"}`}
                    data-testid="listing-type-regular">
                    <Image size={14} className="inline mr-1" /> Regular
                  </button>
                </div>
                <Input placeholder="Title (e.g., Summer Splash Deal)" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} data-testid="special-title-input" />
                <Textarea placeholder="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} data-testid="special-description-input" />
                <div className="grid grid-cols-2 gap-4">
                  {formData.listing_type === "special" && <div>
                    <label className="text-sm text-[#4A5568] mb-1 block">Discount %</label>
                    <Input type="number" min="1" max="100" value={formData.discount_percent} onChange={(e) => setFormData({ ...formData, discount_percent: parseInt(e.target.value) })} data-testid="special-discount-input" />
                  </div>}
                  {formData.listing_type === "special" && <div>
                    <label className="text-sm text-[#4A5568] mb-1 block">Valid Until</label>
                    <Input type="date" value={formData.valid_until} onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })} data-testid="special-date-input" />
                  </div>}
                </div>
                {formData.listing_type === "special" && <Select value={formData.room_type} onValueChange={(v) => setFormData({ ...formData, room_type: v })}>
                  <SelectTrigger data-testid="special-room-select"><SelectValue placeholder="Select Room Type" /></SelectTrigger>
                  <SelectContent>
                    {roomTypes.map((rt) => (
                      <SelectItem key={rt.name} value={rt.name}>{rt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>}
                {/* Image Upload/URL */}
                <div>
                  <label className="text-sm text-[#4A5568] mb-2 block">Special Image (optional)</label>
                  <div className="flex gap-2 bg-[#F7F5F0] p-1 mb-3">
                    <button onClick={() => { setSpecialImageMode("upload"); setFormData((prev) => ({ ...prev, image_url: "" })); }}
                      className={`flex-1 py-2 text-sm font-medium transition-colors ${specialImageMode === "upload" ? "bg-white text-[#0A192F] shadow-sm" : "text-[#4A5568]"}`}
                      data-testid="special-upload-mode-btn">
                      <Image size={14} className="inline mr-1" /> Upload
                    </button>
                    <button onClick={() => { setSpecialImageMode("url"); setFormData((prev) => ({ ...prev, image_url: "" })); }}
                      className={`flex-1 py-2 text-sm font-medium transition-colors ${specialImageMode === "url" ? "bg-white text-[#0A192F] shadow-sm" : "text-[#4A5568]"}`}
                      data-testid="special-url-mode-btn">
                      <Image size={14} className="inline mr-1" /> URL
                    </button>
                  </div>
                  {specialImageMode === "upload" ? (
                    <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-[#0A192F]/20 bg-[#F7F5F0] cursor-pointer hover:border-[#FF6B6B] transition-colors">
                      <input type="file" accept="image/*,.gif" onChange={handleSpecialImageUpload} className="hidden" data-testid="special-file-input" />
                      {uploadingSpecial ? (
                        <p className="text-sm text-[#4A5568]">Uploading...</p>
                      ) : formData.image_url ? (
                        <img src={formData.image_url} alt="Preview" className="h-full object-cover" />
                      ) : (
                        <div className="text-center">
                          <Image size={20} className="mx-auto text-[#4A5568]/50 mb-1" />
                          <p className="text-xs text-[#4A5568]">JPG, PNG, GIF, WebP</p>
                        </div>
                      )}
                    </label>
                  ) : (
                    <Input placeholder="https://..." value={formData.image_url} onChange={(e) => setFormData((prev) => ({ ...prev, image_url: e.target.value }))} data-testid="special-image-url" />
                  )}
                  {formData.image_url && specialImageMode === "url" && (
                    <div className="mt-2 h-20 bg-[#E8E4D9] overflow-hidden rounded"><img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" /></div>
                  )}
                </div>
              </div>
              <DialogFooter className="flex gap-2 sm:gap-3">
                <Button onClick={() => handleCreate(false)} variant="outline" data-testid="save-special-no-email-btn">Create Only</Button>
                <Button onClick={() => handleCreate(true)} className="btn-primary" data-testid="save-special-btn"><Mail size={16} className="mr-2" /> Create & Send Emails</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>}
        </div>
      </div>
      <Card className="card-flamingo">
        <Table>
          <TableHeader><TableRow><TableHead>Image</TableHead><TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Discount</TableHead><TableHead>Room Type</TableHead><TableHead>Valid Until</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {specials.map((special) => (
              <TableRow key={special.id} data-testid={`special-row-${special.id}`}>
                <TableCell>{special.image_url ? <img src={special.image_url} alt="" className="w-12 h-12 object-cover rounded" /> : <div className="w-12 h-12 bg-[#F7F5F0] rounded flex items-center justify-center"><Percent size={16} className="text-[#4A5568]/40" /></div>}</TableCell>
                <TableCell className="font-medium">{special.title}</TableCell>
                <TableCell><span className={`px-2 py-1 text-xs rounded font-bold ${special.listing_type === "regular" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}`}>{special.listing_type === "regular" ? "Regular" : "Special"}</span></TableCell>
                <TableCell>{special.listing_type === "regular" ? "—" : `${special.discount_percent}%`}</TableCell>
                <TableCell>{special.room_type}</TableCell>
                <TableCell>{special.valid_until}</TableCell>
                <TableCell><span className={`px-2 py-1 text-xs rounded ${special.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>{special.active ? "Active" : "Inactive"}</span></TableCell>
                <TableCell>{!viewOnly && special.active && <Button variant="outline" size="sm" onClick={() => handleDeactivate(special.id)} data-testid={`deactivate-special-${special.id}`}><X size={14} /></Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// Gift Draw Tab
const GiftDrawTab = ({ winners, guests, siteSettings, onRefresh, viewOnly, currentUser }) => {
  const [drawing, setDrawing] = useState(false);
  const [prize, setPrize] = useState("$50 Gift Certificate");
  const [showManual, setShowManual] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [viewVoucher, setViewVoucher] = useState(null);

  const handleDeleteWinner = async (winnerId) => {
    try {
      await axios.delete(`${API}/gift-winners/${winnerId}`);
      toast.success("Gift winner deleted");
      onRefresh();
    } catch { toast.error("Failed to delete gift winner"); }
  };

  const filteredGuests = guests.filter((g) =>
    g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const issuedByName = currentUser?.name || currentUser?.username || "Admin";

  const handleRandomDraw = async () => {
    if (guests.length === 0) { toast.error("No registered guests to draw from"); return; }
    setDrawing(true);
    try {
      const res = await axios.post(`${API}/gift-draw?prize=${encodeURIComponent(prize)}&issued_by=${encodeURIComponent(issuedByName)}`);
      toast.success(`Random winner: ${res.data.guest_name}!`);
      onRefresh();
    } catch { toast.error("Draw failed"); }
    setDrawing(false);
  };

  const handleManualSend = async () => {
    if (!selectedGuest) { toast.error("Please select a guest"); return; }
    setDrawing(true);
    try {
      const res = await axios.post(`${API}/gift-draw?prize=${encodeURIComponent(prize)}&guest_id=${selectedGuest.id}&issued_by=${encodeURIComponent(issuedByName)}`);
      toast.success(`Gift certificate sent to ${res.data.guest_name}!`);
      setShowManual(false);
      setSelectedGuest(null);
      setSearchTerm("");
      onRefresh();
    } catch { toast.error("Failed to send gift certificate"); }
    setDrawing(false);
  };

  return (
    <div data-testid="gift-draw-tab">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-heading text-2xl text-[#0A192F]">Gift Certificate Draw</h3>
        <div className="flex gap-3 items-center">
          <Input placeholder="Prize description" value={prize} onChange={(e) => setPrize(e.target.value)} className="w-48" data-testid="prize-input" />
          <Button onClick={handleRandomDraw} className="btn-primary" disabled={drawing} data-testid="draw-btn">
            <Gift size={16} className="mr-2" />{drawing ? "Drawing..." : "Random Draw"}
          </Button>
          <Dialog open={showManual} onOpenChange={setShowManual}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="manual-send-btn">
                <Users size={16} className="mr-2" /> Manual Send
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Send Gift Certificate Manually</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Search Guest</Label>
                  <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setSelectedGuest(null); }}
                    data-testid="guest-search-input"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto border border-[#0A192F]/10" style={{ scrollbarWidth: "thin" }}>
                  {filteredGuests.length > 0 ? filteredGuests.map((guest) => (
                    <div
                      key={guest.id}
                      onClick={() => setSelectedGuest(guest)}
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
                        selectedGuest?.id === guest.id ? "bg-[#FF6B6B]/10 border-l-4 border-[#FF6B6B]" : "hover:bg-[#F7F5F0] border-l-4 border-transparent"
                      }`}
                      data-testid={`guest-pick-${guest.id}`}
                    >
                      <div>
                        <p className="font-medium text-[#0A192F]">{guest.name}</p>
                        <p className="text-xs text-[#4A5568]">{guest.email}</p>
                      </div>
                      {selectedGuest?.id === guest.id && <Check className="text-[#FF6B6B]" size={18} />}
                    </div>
                  )) : (
                    <p className="text-center text-[#4A5568] py-4 text-sm">No guests found</p>
                  )}
                </div>
                {selectedGuest && (
                  <div className="bg-[#F7F5F0] p-3 flex items-center gap-3">
                    <Gift className="text-[#D4AF37]" size={20} />
                    <div>
                      <p className="text-sm font-medium text-[#0A192F]">Sending to: {selectedGuest.name}</p>
                      <p className="text-xs text-[#4A5568]">{selectedGuest.email} — {prize}</p>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={handleManualSend} className="btn-primary" disabled={!selectedGuest || drawing} data-testid="confirm-manual-send-btn">
                  <Gift size={16} className="mr-2" /> {drawing ? "Sending..." : "Send Gift Certificate"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      {/* Gift Voucher Preview Dialog */}
      <Dialog open={!!viewVoucher} onOpenChange={() => setViewVoucher(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl">Gift Voucher Preview</DialogTitle></DialogHeader>
          {viewVoucher && <GiftVoucher winner={viewVoucher} settings={siteSettings} />}
        </DialogContent>
      </Dialog>
      <Card className="card-flamingo">
        <Table>
          <TableHeader><TableRow><TableHead>Winner</TableHead><TableHead>Email</TableHead><TableHead>Prize</TableHead><TableHead>Draw Date</TableHead><TableHead>Issued By</TableHead><TableHead>Notified</TableHead><TableHead>View</TableHead><TableHead>Delete</TableHead></TableRow></TableHeader>
          <TableBody>
            {winners.map((winner) => (
              <TableRow key={winner.id} data-testid={`gift-winner-row-${winner.id}`}>
                <TableCell className="font-medium">{winner.guest_name}</TableCell>
                <TableCell>{winner.guest_email}</TableCell>
                <TableCell>{winner.prize}</TableCell>
                <TableCell>{new Date(winner.draw_date).toLocaleDateString()}</TableCell>
                <TableCell><span className="text-xs font-medium text-[#4A5568]" data-testid={`gift-issued-by-${winner.id}`}>{winner.issued_by || "—"}</span></TableCell>
                <TableCell>{winner.notified ? <Check className="text-green-600" size={18} /> : <X className="text-gray-400" size={18} />}</TableCell>
                <TableCell><Button variant="outline" size="sm" onClick={() => setViewVoucher(winner)} data-testid={`view-gift-${winner.id}`}><Gift size={14} /></Button></TableCell>
                <TableCell>{!viewOnly && <DeleteButton onDelete={() => handleDeleteWinner(winner.id)} label="gift winner" testId={`delete-gift-${winner.id}`} />}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// Trophy Winners Tab
const TrophyTab = ({ winners, guests, siteSettings, onRefresh, viewOnly, currentUser }) => {
  const [showForm, setShowForm] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState("");
  const [prize, setPrize] = useState("Free Night Stay");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [viewCert, setViewCert] = useState(null);

  const issuedByName = currentUser?.name || currentUser?.username || "Admin";

  const handleAward = async () => {
    if (!selectedGuest) { toast.error("Please select a guest"); return; }
    if (!checkIn || !checkOut) { toast.error("Please select check-in and check-out dates"); return; }
    try {
      await axios.post(`${API}/trophy-winner`, {
        guest_id: selectedGuest,
        prize,
        check_in_date: checkIn,
        check_out_date: checkOut,
        issued_by: issuedByName,
      });
      toast.success("Trophy awarded! Email sent with certificate.");
      setShowForm(false);
      setSelectedGuest("");
      setCheckIn("");
      setCheckOut("");
      onRefresh();
    } catch { toast.error("Failed to award trophy"); }
  };

  const handleDelete = async (winnerId) => {
    try {
      await axios.delete(`${API}/trophy-winners/${winnerId}`);
      toast.success("Trophy removed");
      onRefresh();
    } catch { toast.error("Failed to delete"); }
  };

  const formatDateShort = (d) => {
    if (!d) return "—";
    try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return d; }
  };

  return (
    <div data-testid="trophy-tab">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="font-heading text-2xl text-[#0A192F]">Trophy Winners</h3>
          <p className="text-sm text-[#4A5568] mt-1">Award complimentary stays — each generates a unique certificate with QR code</p>
        </div>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild><Button className="btn-primary" data-testid="award-trophy-btn"><Award size={16} className="mr-2" /> Award Trophy</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                <Award className="text-[#D4AF37]" size={24} /> Award Free Night Trophy
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-4">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Guest</Label>
                <Select value={selectedGuest} onValueChange={setSelectedGuest}>
                  <SelectTrigger data-testid="trophy-guest-select"><SelectValue placeholder="Select Guest" /></SelectTrigger>
                  <SelectContent>{guests.map((guest) => <SelectItem key={guest.id} value={guest.id}>{guest.name} ({guest.email})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Prize</Label>
                <Input placeholder="e.g., Free Night Stay" value={prize} onChange={(e) => setPrize(e.target.value)} data-testid="trophy-prize-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Check-In Date</Label>
                  <Input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} data-testid="trophy-checkin-date" />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Check-Out Date</Label>
                  <Input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} data-testid="trophy-checkout-date" />
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={handleAward} className="btn-primary" data-testid="confirm-trophy-btn">Award Trophy</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Certificate Preview Dialog */}
      <Dialog open={!!viewCert} onOpenChange={() => setViewCert(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl">Gift Certificate Preview</DialogTitle></DialogHeader>
          {viewCert && <TrophyCertificate winner={viewCert} settings={siteSettings} />}
        </DialogContent>
      </Dialog>

      {winners.length === 0 ? (
        <Card className="card-flamingo"><CardContent className="p-12 text-center"><Award className="mx-auto text-[#D4AF37]/30 mb-4" size={48} /><p className="text-[#4A5568]">No trophies awarded yet.</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {winners.map((winner) => (
            <div key={winner.id} className="cursor-pointer" onClick={() => setViewCert(winner)}>
              <TrophyCard
                winner={winner}
                formatDateShort={formatDateShort}
                onDelete={viewOnly ? undefined : handleDelete}
                DeleteButton={viewOnly ? undefined : DeleteButton}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Settings Field Component (outside to prevent focus loss)
const SettingsField = ({ label, field, type = "text", placeholder = "", value, onChange }) => (
  <div>
    <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">{label}</Label>
    <Input
      type={type}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={`settings-${field}`}
    />
  </div>
);

// Settings Image Field with Upload + URL
const SettingsImageField = ({ label, field, value, onChange, onUpload, uploading }) => {
  const [mode, setMode] = useState(value && !value.startsWith("/api/files/") ? "url" : "upload");
  return (
    <div>
      <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">{label}</Label>
      <div className="flex gap-2 bg-[#F7F5F0] p-1 mb-2 rounded">
        <button onClick={() => setMode("upload")}
          className={`flex-1 py-1.5 text-xs font-medium transition-colors rounded ${mode === "upload" ? "bg-white text-[#0A192F] shadow-sm" : "text-[#4A5568]"}`}>
          Upload
        </button>
        <button onClick={() => setMode("url")}
          className={`flex-1 py-1.5 text-xs font-medium transition-colors rounded ${mode === "url" ? "bg-white text-[#0A192F] shadow-sm" : "text-[#4A5568]"}`}>
          URL
        </button>
      </div>
      {mode === "upload" ? (
        <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-[#0A192F]/20 bg-[#F7F5F0] cursor-pointer hover:border-[#FF6B6B] transition-colors rounded">
          <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm,.gif" onChange={onUpload} className="hidden" data-testid={`settings-upload-${field}`} />
          {uploading ? (
            <p className="text-sm text-[#4A5568]">Uploading...</p>
          ) : (
            <div className="text-center">
              <p className="text-xs text-[#4A5568]">Click to upload</p>
              <p className="text-xs text-[#4A5568]/50">JPG, PNG, GIF, Video</p>
            </div>
          )}
        </label>
      ) : (
        <Input value={value || ""} onChange={onChange} placeholder="https://..." data-testid={`settings-${field}`} />
      )}
    </div>
  );
};

// Site Settings Tab (NEW)
const SettingsTab = ({ settings, onRefresh }) => {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, form);
      toast.success("Settings saved! Changes are live on the homepage.");
      onRefresh();
    } catch { toast.error("Failed to save settings"); }
    setSaving(false);
  };

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const [uploadingField, setUploadingField] = useState("");
  const handleSettingsUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingField(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateField(field, res.data.url);
      toast.success("Uploaded!");
    } catch { toast.error("Upload failed"); }
    setUploadingField("");
  };

  return (
    <div data-testid="settings-tab">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-heading text-2xl text-[#0A192F]">Site Settings</h3>
        <Button onClick={handleSave} className="btn-primary" disabled={saving} data-testid="save-settings-btn">
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Branding */}
        <Card className="card-flamingo">
          <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Settings size={20} /> Branding</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <SettingsField label="Hotel Name" field="hotel_name" placeholder="Flamingo Inn" value={form.hotel_name} onChange={(e) => updateField("hotel_name", e.target.value)} />
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Tagline</Label>
              <Textarea
                value={form.tagline || ""}
                onChange={(e) => updateField("tagline", e.target.value)}
                placeholder="Your hotel tagline..."
                className="min-h-[80px]"
                data-testid="settings-tagline"
              />
            </div>
            <SettingsImageField label="Logo" field="logo_url" value={form.logo_url} onChange={(e) => updateField("logo_url", e.target.value)} onUpload={(e) => handleSettingsUpload(e, "logo_url")} uploading={uploadingField === "logo_url"} />
            {form.logo_url && (
              <div className="p-4 bg-[#E8E4D9] flex items-center gap-4">
                <img src={form.logo_url} alt="Logo Preview" className="h-12 object-contain" />
                <span className="text-sm text-[#4A5568]">Logo Preview</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Images */}
        <Card className="card-flamingo">
          <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Image size={20} /> Images</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <SettingsImageField label="Hero Background Image" field="hero_image_url" value={form.hero_image_url} onChange={(e) => updateField("hero_image_url", e.target.value)} onUpload={(e) => handleSettingsUpload(e, "hero_image_url")} uploading={uploadingField === "hero_image_url"} />
            {form.hero_image_url && (
              <div className="aspect-video bg-[#E8E4D9] overflow-hidden">
                <img src={form.hero_image_url} alt="Hero Preview" className="w-full h-full object-cover" />
              </div>
            )}
            <SettingsImageField label="Specials Section Background" field="specials_bg_image_url" value={form.specials_bg_image_url} onChange={(e) => updateField("specials_bg_image_url", e.target.value)} onUpload={(e) => handleSettingsUpload(e, "specials_bg_image_url")} uploading={uploadingField === "specials_bg_image_url"} />
            {form.specials_bg_image_url && (
              <div className="aspect-video bg-[#E8E4D9] overflow-hidden">
                <img src={form.specials_bg_image_url} alt="Specials BG Preview" className="w-full h-full object-cover" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="card-flamingo">
          <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Users size={20} /> Contact Info</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <SettingsField label="Address" field="address" placeholder="123 Ocean Drive, Daytona Beach, FL" value={form.address} onChange={(e) => updateField("address", e.target.value)} />
            <SettingsField label="Phone" field="phone" placeholder="(386) 555-0123" value={form.phone} onChange={(e) => updateField("phone", e.target.value)} />
            <SettingsField label="Email" field="email" type="email" placeholder="info@flamingoinn.com" value={form.email} onChange={(e) => updateField("email", e.target.value)} />
            <SettingsField label="Google Review URL" field="google_review_url" placeholder="https://search.google.com/local/writereview?placeid=..." value={form.google_review_url} onChange={(e) => updateField("google_review_url", e.target.value)} />
            <p className="text-xs text-[#4A5568]">Guests who leave a 5-star review will be prompted to also review on Google.</p>
          </CardContent>
        </Card>

        {/* Instagram & Scheduling */}
        <Card className="card-flamingo">
          <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Mail size={20} /> Notifications</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <SettingsField label="Admin Notification Emails" field="admin_notification_emails" placeholder="admin@email.com, manager@email.com" value={form.admin_notification_emails} onChange={(e) => updateField("admin_notification_emails", e.target.value)} />
            <p className="text-xs text-[#4A5568]">Receive an email whenever a new guest registers. Add multiple emails separated by commas.</p>
          </CardContent>
        </Card>

        {/* Instagram & Scheduling */}
        <Card className="card-flamingo">
          <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Instagram size={20} /> Instagram & Auto Draw</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <SettingsField label="Instagram Handle" field="instagram_handle" placeholder="@FlamingoInnDaytona" value={form.instagram_handle} onChange={(e) => updateField("instagram_handle", e.target.value)} />
            <SettingsField label="Instagram Profile URL" field="instagram_url" placeholder="https://www.instagram.com/..." value={form.instagram_url} onChange={(e) => updateField("instagram_url", e.target.value)} />
            <div className="border-t border-[#0A192F]/10 pt-4 mt-4">
              <div className="flex items-center justify-between">
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60">Auto Weekly Draw</Label>
                <Switch
                  checked={form.auto_draw_enabled || false}
                  onCheckedChange={(checked) => updateField("auto_draw_enabled", checked)}
                  data-testid="settings-auto-draw-toggle"
                />
              </div>
              {form.auto_draw_enabled && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label className="text-sm text-[#4A5568] mb-1 block">Day</Label>
                    <Select value={form.auto_draw_day || "sunday"} onValueChange={(v) => updateField("auto_draw_day", v)}>
                      <SelectTrigger data-testid="settings-draw-day"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["monday","tuesday","wednesday","thursday","friday","saturday","sunday"].map((d) => (
                          <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm text-[#4A5568] mb-1 block">Time</Label>
                    <Input type="time" value={form.auto_draw_time || "12:00"} onChange={(e) => updateField("auto_draw_time", e.target.value)} data-testid="settings-draw-time" />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Admin Credentials Section */}
      <div className="mt-8">
        <AdminCredentials />
      </div>

      {/* User Management Section */}
      <div className="mt-8">
        <UserManagement />
      </div>
    </div>
  );
};

// Admin Credentials Change
const AdminCredentials = () => {
  const [showChange, setShowChange] = useState(false);
  const [form, setForm] = useState({ current_password: "", new_username: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.current_password) { toast.error("Enter your current password"); return; }
    if (!form.new_username && !form.new_password) { toast.error("Enter a new username or password"); return; }
    if (form.new_password && form.new_password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (form.new_password && form.new_password !== form.confirm_password) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      const payload = { current_password: form.current_password };
      if (form.new_username) payload.new_username = form.new_username;
      if (form.new_password) payload.new_password = form.new_password;
      await axios.put(`${API}/admin/credentials`, payload);
      toast.success("Admin credentials updated! Use the new login next time.");
      setForm({ current_password: "", new_username: "", new_password: "", confirm_password: "" });
      setShowChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update credentials"); }
    setSaving(false);
  };

  return (
    <div data-testid="admin-credentials">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="font-heading text-2xl text-[#0A192F]">Admin Login Security</h3>
          <p className="text-sm text-[#4A5568] mt-1">Change your admin username and password</p>
        </div>
        <Dialog open={showChange} onOpenChange={setShowChange}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="change-admin-creds-btn">
              <Settings size={16} className="mr-2" /> Change Login
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading text-2xl">Change Admin Credentials</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                You will need to use the new credentials to log in next time.
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Current Password *</Label>
                <Input type="password" value={form.current_password} onChange={(e) => setForm({...form, current_password: e.target.value})} placeholder="Enter current password" data-testid="current-password-input" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">New Username (leave blank to keep current)</Label>
                <Input value={form.new_username} onChange={(e) => setForm({...form, new_username: e.target.value})} placeholder="New username" data-testid="new-admin-username" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">New Password (min 8 characters)</Label>
                <Input type="password" value={form.new_password} onChange={(e) => setForm({...form, new_password: e.target.value})} placeholder="New password" data-testid="new-admin-password" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Confirm New Password</Label>
                <Input type="password" value={form.confirm_password} onChange={(e) => setForm({...form, confirm_password: e.target.value})} placeholder="Confirm new password" data-testid="confirm-admin-password" />
              </div>
            </div>
            <DialogFooter><Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-xs uppercase tracking-widest" data-testid="save-admin-creds-btn">{saving ? "Saving..." : "Update Credentials"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// User Management Component
const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "", name: "", role: "view_only" });
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "", password: "" });

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data);
    } catch { /* empty */ }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async () => {
    if (!newUser.username || !newUser.password) { toast.error("Username and password required"); return; }
    try {
      await axios.post(`${API}/admin/users`, newUser);
      toast.success(`User "${newUser.username}" created`);
      setNewUser({ username: "", password: "", name: "", role: "view_only" });
      setShowAdd(false);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create user"); }
  };

  const handleUpdate = async () => {
    if (!editUser) return;
    const payload = {};
    if (editForm.name) payload.name = editForm.name;
    if (editForm.role) payload.role = editForm.role;
    if (editForm.password) payload.password = editForm.password;
    try {
      await axios.put(`${API}/admin/users/${editUser.id}`, payload);
      toast.success("User updated");
      setEditUser(null);
      fetchUsers();
    } catch { toast.error("Failed to update user"); }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("User deleted");
      fetchUsers();
    } catch { toast.error("Failed to delete user"); }
  };

  return (
    <div data-testid="user-management">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="font-heading text-2xl text-[#0A192F]">User Management</h3>
          <p className="text-sm text-[#4A5568] mt-1">Create manager accounts with view-only or full access</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-user-btn"><Plus size={16} className="mr-2" /> Add User</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading text-2xl">Create New User</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Username</Label>
                <Input value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})} placeholder="manager1" data-testid="new-user-username" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Password</Label>
                <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} placeholder="Enter password" data-testid="new-user-password" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Display Name</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} placeholder="John Manager" data-testid="new-user-name" />
              </div>
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Access Level</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view_only">View Only — Can view all data but cannot edit or delete</SelectItem>
                    <SelectItem value="full_access">Full Access — Can view, edit, create, and delete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={handleCreate} className="btn-primary" data-testid="save-new-user-btn">Create User</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-2xl">Edit User: {editUser?.username}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Display Name</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} data-testid="edit-user-name" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">New Password (leave blank to keep current)</Label>
              <Input type="password" value={editForm.password} onChange={(e) => setEditForm({...editForm, password: e.target.value})} placeholder="Leave blank to keep" data-testid="edit-user-password" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A192F]/60 mb-2 block">Access Level</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({...editForm, role: v})}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="view_only">View Only</SelectItem>
                  <SelectItem value="full_access">Full Access</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={handleUpdate} className="btn-primary" data-testid="save-edit-user-btn">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="card-flamingo">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Username</TableHead><TableHead>Name</TableHead><TableHead>Access Level</TableHead><TableHead>Created</TableHead><TableHead>Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-[#4A5568] py-8">No manager users created yet</TableCell></TableRow>
            ) : users.map((user) => (
              <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                <TableCell className="font-medium">{user.username}</TableCell>
                <TableCell>{user.name}</TableCell>
                <TableCell>
                  <span className={`px-2 py-1 text-xs font-bold rounded ${user.role === "full_access" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                    {user.role === "full_access" ? "Full Access" : "View Only"}
                  </span>
                </TableCell>
                <TableCell>{user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditUser(user); setEditForm({ name: user.name, role: user.role, password: "" }); }} data-testid={`edit-user-${user.id}`}>
                      <Settings size={14} />
                    </Button>
                    <DeleteButton onDelete={() => handleDelete(user.id)} label="user" testId={`delete-user-${user.id}`} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};
const InstagramTab = ({ feed, onRefresh, viewOnly }) => {
  const [showForm, setShowForm] = useState(false);
  const [newPost, setNewPost] = useState({ image_url: "", caption: "", likes: 0, is_winner: false, media_type: "image" });
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState("url"); // "url" or "upload"

  const detectMediaType = (url) => {
    const lower = url.toLowerCase();
    if (lower.match(/\.(mp4|mov|webm)(\?|$)/)) return "video";
    if (lower.match(/\.gif(\?|$)/)) return "gif";
    return "image";
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      const mediaUrl = res.data.url;
      const ext = file.name.split(".").pop().toLowerCase();
      const mediaType = ["mp4", "mov", "webm"].includes(ext) ? "video" : ext === "gif" ? "gif" : "image";
      setNewPost({ ...newPost, image_url: mediaUrl, media_type: mediaType });
      toast.success("File uploaded!");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const handleAdd = async () => {
    if (!newPost.image_url) { toast.error("Please add media (upload or URL)"); return; }
    const postData = { ...newPost };
    if (uploadMode === "url") { postData.media_type = detectMediaType(newPost.image_url); }
    try {
      await axios.post(`${API}/instagram-feed`, postData);
      toast.success("Post added!");
      setShowForm(false);
      setNewPost({ image_url: "", caption: "", likes: 0, is_winner: false, media_type: "image" });
      setUploadMode("url");
      onRefresh();
    } catch { toast.error("Failed to add post"); }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/instagram-feed/${postId}`);
      toast.success("Post deleted");
      onRefresh();
    } catch { toast.error("Failed to delete post"); }
  };

  const handleToggleWinner = async (postId) => {
    try {
      await axios.put(`${API}/instagram-feed/${postId}/toggle-winner`);
      toast.success("Winner status updated");
      onRefresh();
    } catch { toast.error("Failed to update"); }
  };

  return (
    <div data-testid="instagram-tab">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-heading text-2xl text-[#0A192F]">Instagram Feed Management</h3>
        {!viewOnly && (
          <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) { setUploadMode("url"); setNewPost({ image_url: "", caption: "", likes: 0, is_winner: false, media_type: "image" }); } }}>
            <DialogTrigger asChild><Button className="btn-primary" data-testid="add-instagram-post-btn"><Plus size={16} className="mr-2" /> Add Post</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading text-2xl">Add Instagram Post</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                {/* Toggle: Upload or URL */}
                <div className="flex gap-2 bg-[#F7F5F0] p-1">
                  <button
                    onClick={() => { setUploadMode("upload"); setNewPost({ ...newPost, image_url: "" }); }}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${uploadMode === "upload" ? "bg-white text-[#0A192F] shadow-sm" : "text-[#4A5568]"}`}
                    data-testid="upload-mode-btn"
                  >
                    <Image size={14} className="inline mr-1" /> Upload Media
                  </button>
                  <button
                    onClick={() => { setUploadMode("url"); setNewPost({ ...newPost, image_url: "" }); }}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${uploadMode === "url" ? "bg-white text-[#0A192F] shadow-sm" : "text-[#4A5568]"}`}
                    data-testid="url-mode-btn"
                  >
                    <Instagram size={14} className="inline mr-1" /> Media URL
                  </button>
                </div>

                {uploadMode === "upload" ? (
                  <div>
                    <Label className="text-sm text-[#4A5568] mb-2 block">Upload Photo / Video / GIF</Label>
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#0A192F]/20 bg-[#F7F5F0] cursor-pointer hover:border-[#FF6B6B] transition-colors">
                      <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm,.gif" onChange={handleFileUpload} className="hidden" data-testid="insta-file-input" />
                      {uploading ? (
                        <p className="text-sm text-[#4A5568]">Uploading...</p>
                      ) : newPost.image_url ? (
                        newPost.media_type === "video" ? (
                          <video src={newPost.image_url} className="h-full object-cover" muted />
                        ) : (
                          <img src={newPost.image_url} alt="Preview" className="h-full object-cover" />
                        )
                      ) : (
                        <div className="text-center">
                          <Image size={24} className="mx-auto text-[#4A5568]/50 mb-1" />
                          <p className="text-sm text-[#4A5568]">Click to upload</p>
                          <p className="text-xs text-[#4A5568]/50">JPG, PNG, GIF, WebP, MP4, MOV, WebM</p>
                        </div>
                      )}
                    </label>
                  </div>
                ) : (
                  <div>
                    <Label className="text-sm text-[#4A5568] mb-1 block">Media URL (Image, Video, or GIF)</Label>
                    <Input placeholder="https://..." value={newPost.image_url} onChange={(e) => setNewPost({ ...newPost, image_url: e.target.value })} data-testid="insta-image-url" />
                  </div>
                )}

                {newPost.image_url && (
                  <div className="aspect-square w-32 bg-[#E8E4D9] overflow-hidden">
                    {(newPost.media_type === "video" || detectMediaType(newPost.image_url) === "video") ? (
                      <video src={newPost.image_url} className="w-full h-full object-cover" muted controls />
                    ) : (
                      <img src={newPost.image_url} alt="Preview" className="w-full h-full object-cover" />
                    )}
                  </div>
                )}
                <div>
                  <Label className="text-sm text-[#4A5568] mb-1 block">Caption</Label>
                  <Textarea placeholder="Post caption..." value={newPost.caption} onChange={(e) => setNewPost({ ...newPost, caption: e.target.value })} data-testid="insta-caption" />
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={newPost.is_winner} onCheckedChange={(c) => setNewPost({ ...newPost, is_winner: c })} data-testid="insta-winner-toggle" />
                  <Label>Mark as Trophy Winner</Label>
                </div>
              </div>
              <DialogFooter><Button onClick={handleAdd} className="btn-primary" disabled={uploading} data-testid="save-insta-post-btn">Add Post</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <p className="text-sm text-[#4A5568] mb-4">Manage posts displayed in the Instagram feed section. If no custom posts exist, default mock posts will be shown.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {feed.map((post) => (
          <Card key={post.id} className="card-flamingo overflow-hidden" data-testid={`insta-manage-${post.id}`}>
            <div className="aspect-square bg-[#E8E4D9] overflow-hidden relative">
              {post.media_type === "video" ? (
                <video src={post.image_url} className="w-full h-full object-cover" muted loop playsInline autoPlay />
              ) : (
                <img src={post.image_url} alt={post.caption} className="w-full h-full object-cover" />
              )}
              {post.is_winner && (
                <div className="absolute top-2 right-2 bg-[#D4AF37] text-white px-2 py-0.5 text-xs font-bold"><Award size={10} className="inline mr-1" />Winner</div>
              )}
            </div>
            <CardContent className="p-3">
              <p className="text-xs text-[#4A5568] line-clamp-2 mb-2">{post.caption}</p>
              <div className="flex gap-2">
                {!viewOnly && <Button variant="outline" size="sm" onClick={() => handleToggleWinner(post.id)} className="text-xs flex-1" data-testid={`toggle-winner-${post.id}`}>
                  <Award size={12} className="mr-1" /> {post.is_winner ? "Remove" : "Winner"}
                </Button>}
                {!viewOnly && <DeleteButton onDelete={() => handleDelete(post.id)} label="post" testId={`delete-insta-${post.id}`} />}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [stats, setStats] = useState({ total_guests: 0, total_reviews: 0, pending_reviews: 0, approved_reviews: 0, average_rating: 0, gift_winners: 0, trophy_winners: 0, active_specials: 0 });
  const [guests, setGuests] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [giftWinners, setGiftWinners] = useState([]);
  const [trophyWinners, setTrophyWinners] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [instagramFeed, setInstagramFeed] = useState([]);

  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "full_access";
  const isViewOnly = currentUser?.role === "view_only";

  const fetchAllData = async () => {
    try {
      const [statsRes, guestsRes, reviewsRes, specialsRes, giftRes, trophyRes, settingsRes, instaRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/guests`),
        axios.get(`${API}/reviews`),
        axios.get(`${API}/specials?active_only=false`),
        axios.get(`${API}/gift-winners`),
        axios.get(`${API}/trophy-winners`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/instagram-feed`),
      ]);
      setStats(statsRes.data);
      setGuests(guestsRes.data);
      setReviews(reviewsRes.data);
      setSpecials(specialsRes.data.map(s => ({ ...s, image_url: resolveMediaUrl(s.image_url) })));
      setGiftWinners(giftRes.data);
      setTrophyWinners(trophyRes.data);
      setSiteSettings(settingsRes.data);
      setInstagramFeed(instaRes.data.map(p => ({ ...p, image_url: resolveMediaUrl(p.image_url) })));
    } catch (err) { console.error("Error fetching admin data:", err); }
  };

  useEffect(() => { if (isLoggedIn) fetchAllData(); }, [isLoggedIn]);

  if (!isLoggedIn) return <AdminLogin onLogin={(data) => { setCurrentUser(data); setIsLoggedIn(true); }} />;

  return (
    <div className="admin-grid" data-testid="admin-dashboard">
      <aside className="admin-sidebar p-6 hidden md:block">
        <h2 className="font-heading text-2xl mb-4">Flamingo Inn</h2>
        <div className="mb-6 px-4">
          <p className="text-white/80 text-sm font-medium">{currentUser?.name || currentUser?.username}</p>
          <p className="text-xs text-white/40 uppercase tracking-wider">{currentUser?.role === "admin" ? "Admin" : currentUser?.role === "full_access" ? "Full Access" : "View Only"}</p>
        </div>
        <nav className="space-y-2">
          <a href="/" className="flex items-center gap-3 px-4 py-3 text-white/60 hover:text-white hover:bg-white/10 transition-colors">
            <LayoutDashboard size={20} /> Back to Site
          </a>
        </nav>
        <div className="mt-auto pt-8">
          <Button variant="ghost" className="w-full justify-start text-white/60 hover:text-white hover:bg-white/10" onClick={() => { setIsLoggedIn(false); setCurrentUser(null); }} data-testid="logout-btn">
            <LogOut size={20} className="mr-3" /> Logout
          </Button>
        </div>
      </aside>

      <main className="admin-content p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="font-heading text-4xl text-[#0A192F]">Admin Dashboard</h1>
            {isViewOnly && <span className="px-3 py-1 text-xs font-bold bg-amber-100 text-amber-800 rounded uppercase tracking-wider">View Only Mode</span>}
          </div>
          <StatsCards stats={stats} />
          <Tabs defaultValue="guests" className="space-y-6">
            <TabsList className="bg-white border border-[#0A192F]/10 flex-wrap">
              <TabsTrigger value="guests" data-testid="tab-guests"><Users size={16} className="mr-2" /> Guests</TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews"><Star size={16} className="mr-2" /> Reviews</TabsTrigger>
              <TabsTrigger value="specials" data-testid="tab-specials"><Percent size={16} className="mr-2" /> Specials</TabsTrigger>
              <TabsTrigger value="gifts" data-testid="tab-gifts"><Gift size={16} className="mr-2" /> Gift Draw</TabsTrigger>
              <TabsTrigger value="trophies" data-testid="tab-trophies"><Award size={16} className="mr-2" /> Trophies</TabsTrigger>
              <TabsTrigger value="instagram" data-testid="tab-instagram"><Instagram size={16} className="mr-2" /> Instagram</TabsTrigger>
              {currentUser?.role === "admin" && <TabsTrigger value="settings" data-testid="tab-settings"><Settings size={16} className="mr-2" /> Settings</TabsTrigger>}
            </TabsList>
            <TabsContent value="guests"><GuestsTab guests={guests} reviews={reviews} giftWinners={giftWinners} trophyWinners={trophyWinners} onRefresh={fetchAllData} viewOnly={isViewOnly} /></TabsContent>
            <TabsContent value="reviews"><ReviewsTab reviews={reviews} onRefresh={fetchAllData} viewOnly={isViewOnly} /></TabsContent>
            <TabsContent value="specials"><SpecialsTab specials={specials} onRefresh={fetchAllData} viewOnly={isViewOnly} /></TabsContent>
            <TabsContent value="gifts"><GiftDrawTab winners={giftWinners} guests={guests} siteSettings={siteSettings} onRefresh={fetchAllData} viewOnly={isViewOnly} currentUser={currentUser} /></TabsContent>
            <TabsContent value="trophies"><TrophyTab winners={trophyWinners} guests={guests} siteSettings={siteSettings} onRefresh={fetchAllData} viewOnly={isViewOnly} currentUser={currentUser} /></TabsContent>
            <TabsContent value="instagram"><InstagramTab feed={instagramFeed} onRefresh={fetchAllData} viewOnly={isViewOnly} /></TabsContent>
            {currentUser?.role === "admin" && <TabsContent value="settings"><SettingsTab settings={siteSettings} onRefresh={fetchAllData} /></TabsContent>}
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
